/**
 * LegalTemplates PDF Editor — annotation layer: drawing, selection, history
 *
 * Split from the former public/js/pdf-editor.js IIFE (Phase 1, Sep 2026).
 * Page config is injected by the Blade view as window.pdfEditorConfig; pdf.js
 * and pdf-lib are bundled from npm through context.js (no CDN).
 */
import {
    colorInput,
    imageInput,
    pagesEl,
    publicEditorIsAuthenticated,
    publicEditorMode,
    publicEditorSignatureUnlocked,
    publicEditorSignatureUpgradeUrl,
    publicEditorSignatureUrl,
    publicEditorUploadButton,
    publicEditorUploadInput,
    publicEditorMaxPages,
    publicEditorUploadMaxMb,
    publicEditorUploadUrl,
    redoButton,
    replaceToolEnabled,
    session,
    sizeInput,
    sizeValueEl,
    state,
    suiteModeLabels,
    undoButton,
} from './context.js';
import {
    clamp,
    distancePointToSegment,
    finiteNumber,
    generateId,
    normalizeBackgroundColor,
    normalizeInkColor,
    setButtonState,
} from './core.js';
import { pinDocumentUrl } from './core.js';
import { explainAtPoint } from './assist.js';
import { scheduleDraftSave } from './drafts.js';
import { getFindOverlaySvg } from './find.js';
import { noteUnsupportedScripts } from './fonts.js';
import { syncPropsPanel } from './props.js';
import {
    commitInlineFormFieldEditor,
    findFormFieldAtPoint,
    getFormFieldOverlaySvg,
    getFormFieldsForPage,
    openInlineFormFieldEditor,
    toggleCheckboxFormField,
    updateFormFillSummary,
} from './form-fill.js';
import {
    getApiErrorMessage,
    getCsrfToken,
    hasPdfHeader,
    hideUploadEmptyState,
    persistDocumentBytes,
    resetDocumentEditingStateForUpload,
    showUploadEmptyState,
} from './io.js';
import { getWatermarkPreviewSvg, openPublicAuthModal, syncWatermarkInputUi, syncWatermarkTabUi } from './modals.js';
import {
    buildReplacementFragment,
    clampReplaceMaskToNeighbours,
    clearTextSelection,
    commitAllInlineEditors,
    commitBlockTextEditor,
    commitInlineCommentEditor,
    commitInlineReplaceEditor,
    commitInlineTextEditor,
    findEditBlockAnnotationAtPoint,
    findReplaceAnnotationAtPoint,
    findTextBlockAtPoint,
    findTextBlockById,
    findTextFragmentAtPoint,
    fitReplaceText,
    fontFamilyToCss,
    getEditBlockBounds,
    getEditBlockSvg,
    getReplaceInkBox,
    getTextAnnotationBounds,
    openBlockTextEditor,
    openInlineCommentEditor,
    openInlineReplaceEditor,
    pageTextLooksUnreadable,
    openInlineTextEditor,
    syncTextControlButtons,
} from './text.js';
import {
    activateTool,
    getToolColor,
    getToolSize,
    setHint,
    setStatus,
    showAlert,
    syncDocumentNameUi,
} from './ui.js';
import {
    countPdfPages,
    ensurePagePrepared,
    getPagePoint,
    isPagePrepared,
    renderAllPages,
    setActiveThumb,
    syncPageIndicator,
} from './viewport.js';

export function handlePublicSignatureAccess() {
    if (!publicEditorMode) {
        return false;
    }

    if (publicEditorSignatureUnlocked) {
        if (publicEditorSignatureUrl) {
            setStatus('Opening E-Sign suite...');
            showAlert('Opening E-Sign suite for signature setup.');
            window.setTimeout(() => {
                window.location.href = publicEditorSignatureUrl;
            }, 180);
            return true;
        }

        showAlert('Signature is unlocked on your plan. Open the E-Sign suite to continue.', true);
        setStatus('Open E-Sign suite');
        return true;
    }

    if (!publicEditorIsAuthenticated) {
        showAlert('Sign in or register to use Signature tools.', true);
        setStatus('Login required for signature');
        openPublicAuthModal('signature');
        return true;
    }

    if (publicEditorSignatureUpgradeUrl) {
        showAlert('Signature is a Pro feature. Redirecting to plans...', true);
        setStatus('Membership required for signature');
        window.setTimeout(() => {
            window.location.href = publicEditorSignatureUpgradeUrl;
        }, 220);
        return true;
    }

    showAlert('Signature is a Pro feature and requires an active monthly or yearly plan.', true);
    setStatus('Membership required for signature');
    return true;
}

export async function handlePublicPdfUpload(file) {
    if (!file) {
        return;
    }

    const maxBytes = Math.max(1, Number(publicEditorUploadMaxMb || 25)) * 1024 * 1024;
    const mimeType = String(file.type || '').toLowerCase();
    const hasPdfMime = mimeType === 'application/pdf';
    const hasPdfExt = /\.pdf$/i.test(String(file.name || ''));

    if (!hasPdfMime && !hasPdfExt) {
        showAlert('Only PDF files are supported for this editor.', true);
        return;
    }

    if (file.size > maxBytes) {
        showAlert(`File is too large. Maximum upload size is ${publicEditorUploadMaxMb} MB.`, true);
        return;
    }

    setStatus('Uploading PDF...');
    setButtonState(publicEditorUploadButton, true);

    try {
        const baseName = String(file.name || 'Uploaded PDF')
            .replace(/\.pdf$/i, '')
            .replace(/\s+/g, ' ')
            .trim() || 'Uploaded PDF';
        const bytes = new Uint8Array(await file.arrayBuffer());
        if (!hasPdfHeader(bytes)) {
            throw new Error('That file does not appear to be a valid PDF.');
        }
        // One page budget everywhere, checked here before any bytes travel.
        const pageCount = await countPdfPages(bytes);
        if (pageCount > publicEditorMaxPages) {
            throw new Error(`This PDF has ${pageCount} pages. The editor opens PDFs up to ${publicEditorMaxPages} pages; split it first with the Split tool.`);
        }

        if (publicEditorUploadUrl) {
            const formData = new FormData();
            formData.append('file', file, `${baseName}.pdf`);
            formData.append('title', baseName);

            const response = await fetch(publicEditorUploadUrl, {
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN': getCsrfToken(),
                    'Accept': 'application/json',
                },
                body: formData,
                credentials: 'same-origin',
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(getApiErrorMessage(payload, 'Could not upload PDF.'));
            }

            resetDocumentEditingStateForUpload();
            session.sourceUrl = payload.source_url || session.sourceUrl;
            session.docName = String(payload.doc_name || baseName);
            if (payload.doc_id) {
                session.docId = String(payload.doc_id);
                pinDocumentUrl();
            }
            state.basePdfBytes = null;

            hideUploadEmptyState();
            syncDocumentNameUi();
            updateUndoRedoButtons();
            await renderAllPages();
            setHint(state.activeTool);
            setStatus('Editor ready');
            showAlert(payload.message || 'PDF uploaded and saved to your editor session.');
        } else {
            resetDocumentEditingStateForUpload();
            session.sourceUrl = null;
            session.docName = baseName;
            state.basePdfBytes = bytes;

            hideUploadEmptyState();
            syncDocumentNameUi();
            updateUndoRedoButtons();
            await renderAllPages();
            setHint(state.activeTool);
            setStatus('Editor ready');
            showAlert('PDF uploaded. You can now edit this file in your browser.');
        }
    } catch (error) {
        showAlert(error.message || 'Could not upload that PDF. Please try another file.', true);
        setStatus('Upload failed');
        if (publicEditorMode && !session.sourceUrl && !state.basePdfBytes) {
            showUploadEmptyState();
        }
    } finally {
        setButtonState(publicEditorUploadButton, false);
        if (publicEditorUploadInput) {
            publicEditorUploadInput.value = '';
        }
    }
}

export function hasPendingAnnotationChanges() {
    return !!state.annotationDirty;
}

export function escapeSvg(text) {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function isLikelyUnderlineRun(runText) {
    const value = String(runText || '').replace(/\s+/g, '');
    if (value.length < 3) {
        return false;
    }

    const lineChars = (value.match(/[_\u2014\u2013\-]/g) || []).length;
    const lineRatio = lineChars / Math.max(1, value.length);
    return lineRatio >= 0.75;
}

export function getTextAnnotationContentRect(annotation) {
    const bounds = getTextAnnotationBounds(annotation);
    const rawText = String(annotation && annotation.text ? annotation.text : '').replace(/\r/g, '');
    if (!rawText.trim()) {
        return null;
    }

    const lines = rawText.split(/\n/);
    const fontSize = Math.max(10, Number(annotation && annotation.fontSize ? annotation.fontSize : bounds.fontSize));
    const averageGlyphWidth = Math.max(4.8, fontSize * 0.56);
    const maxLineLength = lines.reduce((maxLength, line) => Math.max(maxLength, line.length), 0);
    const estimatedTextWidth = Math.max(14, Math.round(maxLineLength * averageGlyphWidth));
    const horizontalPadding = 6;
    const contentWidth = Math.max(16, Math.min(bounds.width, estimatedTextWidth + (horizontalPadding * 2)));

    let contentX = bounds.x + horizontalPadding;
    const align = String(annotation && annotation.align ? annotation.align : 'left').toLowerCase();
    if (align === 'center') {
        contentX = bounds.x + ((bounds.width - contentWidth) / 2);
    } else if (align === 'right') {
        contentX = (bounds.x + bounds.width) - contentWidth - horizontalPadding;
    }
    contentX = clamp(contentX, bounds.x, bounds.x + Math.max(0, bounds.width - contentWidth));

    const lineHeight = Math.max(12, Math.round(fontSize * 1.25));
    const contentHeight = Math.max(fontSize + 6, Math.min(bounds.height, (lines.length * lineHeight) + 4));

    return {
        x: contentX,
        y: bounds.y,
        width: contentWidth,
        height: contentHeight,
    };
}

export function getSelectedTextAnnotation() {
    if (!state.selectedAnnotationId) {
        return null;
    }

    return state.annotations.find((annotation) => annotation.id === state.selectedAnnotationId && annotation.type === 'text') || null;
}

export function getCommentAnnotationBounds(annotation) {
    const pinSize = Math.max(18, Number(annotation.pinSize || 22));

    return {
        x: Number(annotation.x || 0),
        y: Number(annotation.y || 0),
        width: pinSize,
        height: pinSize,
        pinSize,
    };
}

export function getImageAnnotationBounds(annotation) {
    return {
        x: Number(annotation.x || 0),
        y: Number(annotation.y || 0),
        width: Math.max(1, Number(annotation.width || 1)),
        height: Math.max(1, Number(annotation.height || 1)),
    };
}

export function getImageResizeHandle(annotation) {
    const bounds = getImageAnnotationBounds(annotation);
    const size = Math.max(10, Math.min(14, Math.min(bounds.width, bounds.height) * 0.22));
    return {
        x: bounds.x + bounds.width - size,
        y: bounds.y + bounds.height - size,
        width: size,
        height: size,
        size,
    };
}

export function syncStyleControlsFromAnnotation(annotation) {
    if (!annotation || annotation.type !== 'text') {
        return;
    }

    state.textStyle.fontFamily = annotation.fontFamily || 'Helvetica';
    state.textStyle.bold = !!annotation.bold;
    state.textStyle.italic = !!annotation.italic;
    state.textStyle.underline = !!annotation.underline;
    state.textStyle.align = annotation.align || 'left';

    const fontSize = Math.max(1, Math.min(40, Math.round(Number(annotation.fontSize || 16))));
    sizeInput.value = String(fontSize);
    sizeValueEl.textContent = String(fontSize);

    if (/^#[0-9A-Fa-f]{6}$/.test(String(annotation.color || ''))) {
        colorInput.value = annotation.color;
    }

    syncTextControlButtons();
}

export function setSelectedAnnotation(annotationId, syncControls = true) {
    if (state.selectedAnnotationId === annotationId) {
        return;
    }

    state.selectedAnnotationId = annotationId || null;

    if (syncControls && state.selectedAnnotationId) {
        const selected = getSelectedTextAnnotation();
        if (selected) {
            syncStyleControlsFromAnnotation(selected);
        }
    }

    redrawAllPages();
}

export function ensureSelectionStillExists() {
    if (!state.selectedAnnotationId) {
        return;
    }

    const exists = state.annotations.some((annotation) => annotation.id === state.selectedAnnotationId);
    if (!exists) {
        state.selectedAnnotationId = null;
    }
}

export function findTextAnnotationAtPoint(pageNumber, x, y) {
    for (let index = state.annotations.length - 1; index >= 0; index--) {
        const annotation = state.annotations[index];
        if (annotation.type !== 'text' || annotation.page !== pageNumber) {
            continue;
        }

        const bounds = getTextAnnotationBounds(annotation);
        const withinX = x >= bounds.x && x <= (bounds.x + bounds.width);
        const withinY = y >= bounds.y && y <= (bounds.y + bounds.height);

        if (withinX && withinY) {
            return annotation;
        }
    }

    return null;
}

export function findCommentAnnotationAtPoint(pageNumber, x, y) {
    for (let index = state.annotations.length - 1; index >= 0; index--) {
        const annotation = state.annotations[index];
        if (annotation.type !== 'comment' || annotation.page !== pageNumber) {
            continue;
        }

        const bounds = getCommentAnnotationBounds(annotation);
        const withinX = x >= bounds.x && x <= (bounds.x + bounds.width);
        const withinY = y >= bounds.y && y <= (bounds.y + bounds.height);

        if (withinX && withinY) {
            return annotation;
        }
    }

    return null;
}

export function findImageAnnotationAtPoint(pageNumber, x, y) {
    for (let index = state.annotations.length - 1; index >= 0; index--) {
        const annotation = state.annotations[index];
        if (annotation.type !== 'image' || annotation.page !== pageNumber) {
            continue;
        }

        const bounds = getImageAnnotationBounds(annotation);
        const withinX = x >= bounds.x && x <= (bounds.x + bounds.width);
        const withinY = y >= bounds.y && y <= (bounds.y + bounds.height);

        if (withinX && withinY) {
            return annotation;
        }
    }

    return null;
}

export function getAnnotationTypeLabel(annotation) {
    if (!annotation || !annotation.type) {
        return 'Element';
    }

    if (annotation.type === 'text') {
        return 'Text';
    }

    if (annotation.type === 'comment') {
        return 'Comment';
    }

    if (annotation.type === 'image') {
        return 'Image';
    }

    if (annotation.type === 'replace-text') {
        return 'Replacement';
    }

    if (annotation.type === 'edit-block') {
        return 'Text edit';
    }

    if (annotation.type === 'highlight') {
        return 'Highlight';
    }

    if (annotation.type === 'shape') {
        return 'Shape';
    }

    if (annotation.type === 'underline') {
        return 'Underline';
    }

    if (annotation.type === 'draw') {
        return 'Draw';
    }

    return 'Element';
}

export function annotationContainsPoint(annotation, x, y) {
    if (!annotation) {
        return false;
    }

    if (annotation.type === 'text') {
        const bounds = getTextAnnotationBounds(annotation);
        return x >= bounds.x && x <= (bounds.x + bounds.width) && y >= bounds.y && y <= (bounds.y + bounds.height);
    }

    if (annotation.type === 'comment') {
        const bounds = getCommentAnnotationBounds(annotation);
        return x >= bounds.x && x <= (bounds.x + bounds.width) && y >= bounds.y && y <= (bounds.y + bounds.height);
    }

    if (annotation.type === 'image') {
        const bounds = getImageAnnotationBounds(annotation);
        return x >= bounds.x && x <= (bounds.x + bounds.width) && y >= bounds.y && y <= (bounds.y + bounds.height);
    }

    if (annotation.type === 'edit-block') {
        const bounds = getEditBlockBounds(annotation);
        return x >= bounds.x - 3 && x <= (bounds.x + bounds.width + 3)
            && y >= bounds.y - 3 && y <= (bounds.y + bounds.height + 3);
    }

    if (annotation.type === 'replace-text') {
        const text = String(annotation.text || '');
        if (!text) {
            return x >= annotation.x && x <= (annotation.x + annotation.width) && y >= annotation.y && y <= (annotation.y + annotation.height);
        }

        const fontSize = Math.max(8, Number(annotation.fontSize || 12));
        const visibleWidth = Math.min(
            Number(annotation.width || 1),
            Math.max(10, text.length * Math.max(4.2, fontSize * 0.54))
        );
        const padX = Math.max(2, fontSize * 0.12);
        const padY = Math.max(2, fontSize * 0.16);

        return x >= (annotation.x - padX)
            && x <= (annotation.x + visibleWidth + padX)
            && y >= (annotation.y - padY)
            && y <= (annotation.y + annotation.height + padY);
    }

    if (annotation.type === 'shape' || annotation.type === 'highlight' || annotation.type === 'erase' || annotation.type === 'redact') {
        return x >= annotation.x && x <= (annotation.x + annotation.width) && y >= annotation.y && y <= (annotation.y + annotation.height);
    }

    if (annotation.type === 'underline') {
        const pad = Math.max(4, Number(annotation.stroke || 1) + 2);
        const minX = annotation.x;
        const maxX = annotation.x + annotation.width;
        const lineY = annotation.y + Number(annotation.stroke || 1);
        return x >= minX && x <= maxX && y >= (lineY - pad) && y <= (lineY + pad);
    }

    if (annotation.type === 'draw') {
        const points = Array.isArray(annotation.points) ? annotation.points : [];
        if (points.length < 2) {
            return false;
        }

        const tolerance = Math.max(5, Number(annotation.stroke || 1) + 3);
        for (let index = 1; index < points.length; index += 1) {
            const start = points[index - 1];
            const end = points[index];
            const distance = distancePointToSegment(
                x,
                y,
                Number(start.x || 0),
                Number(start.y || 0),
                Number(end.x || 0),
                Number(end.y || 0)
            );

            if (distance <= tolerance) {
                return true;
            }
        }
    }

    return false;
}

export function findSelectableAnnotationAtPoint(pageNumber, x, y) {
    for (let index = state.annotations.length - 1; index >= 0; index -= 1) {
        const annotation = state.annotations[index];
        if (annotation.page !== pageNumber) {
            continue;
        }

        if (annotationContainsPoint(annotation, x, y)) {
            return annotation;
        }
    }

    return null;
}

export function getSelectedAnnotation() {
    if (!state.selectedAnnotationId) {
        return null;
    }

    return state.annotations.find((annotation) => annotation.id === state.selectedAnnotationId) || null;
}

export function deleteSelectedAnnotation() {
    const selected = getSelectedAnnotation();
    if (!selected) {
        return false;
    }

    pushHistory();
    state.annotations = state.annotations.filter((annotation) => annotation.id !== selected.id);
    state.selectedAnnotationId = null;
    redrawAllPages();
    setStatus(`${getAnnotationTypeLabel(selected)} deleted`);
    return true;
}

export function isPointInImageResizeHandle(annotation, x, y) {
    const handle = getImageResizeHandle(annotation);
    const withinX = x >= handle.x && x <= (handle.x + handle.width);
    const withinY = y >= handle.y && y <= (handle.y + handle.height);
    return withinX && withinY;
}

export function applyStyleToSelectedTextAnnotation() {
    if (state.activeSuiteTool !== 'edit' || state.activeTool !== 'text') {
        return false;
    }

    if (state.activeTextEditor) {
        return false;
    }

    const selected = getSelectedTextAnnotation();
    if (!selected) {
        return false;
    }

    pushHistory();
    selected.fontFamily = state.textStyle.fontFamily;
    selected.bold = !!state.textStyle.bold;
    selected.italic = !!state.textStyle.italic;
    selected.underline = !!state.textStyle.underline;
    selected.align = state.textStyle.align;
    selected.fontSize = Math.max(10, getToolSize());
    selected.color = getToolColor();
    selected.boxWidth = Math.max(120, Number(selected.boxWidth || 240));

    redrawPage(selected.page);
    setStatus('Text style updated');
    return true;
}

// ------------------------------------------------------------------
// Operation log (undo / redo)
//
// Every entry restores a complete edit state: annotations, form-field
// values and the staged watermark. Page operations (delete pages, merge)
// push a "document" entry that also carries the document bytes before and
// after, so undoing them puts the previous file back — on the server too.
// ------------------------------------------------------------------
const HISTORY_LIMIT = 80;
const DOCUMENT_HISTORY_LIMIT = 5;

function watermarkSnapshot() {
    const { appliedBaseBytes, busy, ...rest } = state.watermark;
    return rest;
}

export function captureEditSnapshot() {
    return JSON.stringify({
        annotations: state.annotations,
        formFieldOverrides: Array.from(state.formFieldOverrides.entries()),
        watermark: watermarkSnapshot(),
    });
}

export function restoreEditSnapshot(json) {
    let snapshot;
    try {
        snapshot = JSON.parse(json);
    } catch (error) {
        return;
    }
    if (Array.isArray(snapshot)) {
        // Legacy shape: annotations only.
        state.annotations = snapshot;
        return;
    }
    state.annotations = Array.isArray(snapshot.annotations) ? snapshot.annotations : [];
    state.formFieldOverrides.clear();
    (Array.isArray(snapshot.formFieldOverrides) ? snapshot.formFieldOverrides : []).forEach((entry) => {
        if (Array.isArray(entry) && entry.length >= 2) {
            state.formFieldOverrides.set(entry[0], entry[1]);
        }
    });
    if (snapshot.watermark && typeof snapshot.watermark === 'object') {
        Object.assign(state.watermark, snapshot.watermark);
        syncWatermarkInputUi();
        syncWatermarkTabUi();
    }
}

function trimHistory() {
    if (state.history.length > HISTORY_LIMIT) {
        state.history.splice(0, state.history.length - HISTORY_LIMIT);
    }
    // Document entries hold whole PDFs; keep only the most recent few and
    // drop everything older than the oldest kept one.
    const documentIndexes = state.history
        .map((entry, index) => (entry && entry.kind === 'document' ? index : -1))
        .filter((index) => index >= 0);
    if (documentIndexes.length > DOCUMENT_HISTORY_LIMIT) {
        const cutoff = documentIndexes[documentIndexes.length - DOCUMENT_HISTORY_LIMIT];
        state.history.splice(0, cutoff);
    }
}

export function pushHistory(label = 'edit') {
    state.history.push({ kind: 'edit', label, snapshot: captureEditSnapshot() });
    trimHistory();
    state.redo = [];
    state.annotationDirty = true;
    updateUndoRedoButtons();
    scheduleDraftSave();
    // Characters the bundled fonts cannot embed are flagged as they appear.
    noteUnsupportedScripts();
}

/**
 * Record a completed page operation. `beforeBytes`/`afterBytes` are the
 * document before and after; the snapshots are the edit state at each point.
 */
export function pushDocumentHistory({ label, beforeBytes, afterBytes, beforeSnapshot, afterSnapshot }) {
    if (!beforeBytes || !afterBytes) {
        return;
    }
    state.history.push({
        kind: 'document',
        label: label || 'page change',
        beforeBytes,
        afterBytes,
        beforeSnapshot: beforeSnapshot || captureEditSnapshot(),
        afterSnapshot: afterSnapshot || captureEditSnapshot(),
    });
    trimHistory();
    state.redo = [];
    updateUndoRedoButtons();
}

function entryLabel(entry) {
    return entry && entry.label && entry.label !== 'edit' ? ` ${entry.label}` : '';
}

export function updateUndoRedoButtons() {
    const nextUndo = state.history[state.history.length - 1];
    const nextRedo = state.redo[state.redo.length - 1];
    undoButton.disabled = state.history.length === 0 || !!state.historyBusy;
    redoButton.disabled = state.redo.length === 0 || !!state.historyBusy;
    undoButton.title = nextUndo ? `Undo${entryLabel(nextUndo)}` : 'Nothing to undo';
    redoButton.title = nextRedo ? `Redo${entryLabel(nextRedo)}` : 'Nothing to redo';
}

async function applyDocumentEntry(entry, direction) {
    const targetBytes = direction === 'undo' ? entry.beforeBytes : entry.afterBytes;
    const targetSnapshot = direction === 'undo' ? entry.beforeSnapshot : entry.afterSnapshot;
    state.historyBusy = true;
    updateUndoRedoButtons();
    setStatus(`${direction === 'undo' ? 'Undoing' : 'Redoing'} ${entry.label}...`);
    try {
        await persistDocumentBytes(targetBytes);
        restoreEditSnapshot(targetSnapshot);
        state.selectedAnnotationId = null;
        state.annotationDirty = state.annotations.length > 0 || state.formFieldOverrides.size > 0;
        (direction === 'undo' ? state.redo : state.history).push(entry);
        redrawAllPages();
        updateFormFillSummary();
        scheduleDraftSave();
        setStatus(`${direction === 'undo' ? 'Undo' : 'Redo'} complete: ${entry.label}`);
    } catch (error) {
        // Put the entry back where it came from so the person can retry.
        (direction === 'undo' ? state.history : state.redo).push(entry);
        showAlert(error.message || `Could not ${direction} ${entry.label}.`, true);
        setStatus(`${direction === 'undo' ? 'Undo' : 'Redo'} failed`);
    } finally {
        state.historyBusy = false;
        updateUndoRedoButtons();
    }
}

export function getAnnotationSvg(annotation) {
    if (!annotation) {
        return '';
    }

    if (annotation.type === 'text') {
        const bounds = getTextAnnotationBounds(annotation);
        const lines = String(annotation.text || '').split(/\n/);
        const fontSize = Math.max(10, Number(annotation.fontSize || 16));
        const lineHeight = Math.round(fontSize * 1.25);
        const boxWidth = bounds.width;
        const align = annotation.align || 'left';
        const textAnchor = align === 'center' ? 'middle' : (align === 'right' ? 'end' : 'start');
        const x = align === 'center'
            ? annotation.x + (boxWidth / 2)
            : (align === 'right' ? annotation.x + boxWidth : annotation.x);
        const fontFamily = fontFamilyToCss(annotation.fontFamily || 'Helvetica');
        const fontWeight = annotation.bold ? '700' : '400';
        const fontStyle = annotation.italic ? 'italic' : 'normal';
        const textDecoration = annotation.underline ? 'underline' : 'none';

        const tSpans = lines.map((line, index) => {
            const dy = index === 0 ? fontSize : lineHeight;
            return `<tspan x="${x}" dy="${dy}">${escapeSvg(line.length ? line : ' ')}</tspan>`;
        }).join('');

        const isSelected = annotation.id === state.selectedAnnotationId
            && state.activeSuiteTool === 'edit'
            && (state.activeTool === 'text' || state.activeTool === 'pointer');
        const selectionBox = isSelected
            ? `<rect x="${bounds.x}" y="${bounds.y}" width="${bounds.width}" height="${bounds.height}" fill="rgba(59,130,246,0.08)" stroke="#3b82f6" stroke-width="1.2" stroke-dasharray="4 3"></rect>`
            : '';

        return `${selectionBox}<text x="${x}" y="${annotation.y}" fill="${annotation.color}" font-size="${fontSize}" font-family="${fontFamily}" font-weight="${fontWeight}" font-style="${fontStyle}" text-decoration="${textDecoration}" text-anchor="${textAnchor}">${tSpans}</text>`;
    }

    if (annotation.type === 'edit-block') {
        return getEditBlockSvg(annotation);
    }

    if (annotation.type === 'replace-text') {
        if (
            state.activeReplaceEditor
            && state.activeReplaceEditor.annotationId
            && state.activeReplaceEditor.annotationId === annotation.id
        ) {
            return '';
        }

        const text = String(annotation.text || '');
        const fontSize = Math.max(8, Number(annotation.fontSize || 12));
        const fontFamily = annotation.cssFontFamily || fontFamilyToCss(annotation.fontFamily || 'Helvetica');
        const fontFamilyAttr = escapeSvg(fontFamily);
        const fontWeight = annotation.bold ? '700' : '400';
        const fontStyle = annotation.italic ? 'italic' : 'normal';
        const fillColor = normalizeBackgroundColor(annotation.backgroundColor || '#ffffff');

        // Tighten the mask to the glyph ink box so it never clips the line above/below (bug #1),
        // and clamp its right edge to the containing cell so it can't erase the neighbour (bug #2).
        const inkBox = getReplaceInkBox(annotation);
        const maskBounds = clampReplaceMaskToNeighbours(annotation.page, annotation, inkBox);
        const rectPadX = 1;
        const rectPadY = 0.4;
        const rectX = Math.max(0, annotation.x - rectPadX);
        const rectY = Math.max(0, maskBounds.top - rectPadY);
        const rectHeight = Math.max(1, (maskBounds.bottom - maskBounds.top) + (rectPadY * 2));
        const storedRightBoundary = finiteNumber(annotation.rightBoundary, Infinity);
        const boundaryRight = Number.isFinite(storedRightBoundary)
            ? storedRightBoundary - 0.5
            : Infinity;
        const rawRectRight = annotation.x + annotation.width + rectPadX;
        const rectRight = Math.min(rawRectRight, boundaryRight);
        const rectWidth = Math.max(1, rectRight - rectX);

        // Shrink-to-fit + ellipsis, identical to the PDF export, so preview == download (bug #3).
        const fitFont = { family: fontFamily, weight: fontWeight, style: fontStyle, size: fontSize };
        const availableWidth = Math.max(4, Math.min(annotation.width, rectRight - annotation.x));
        const fitted = fitReplaceText(text, fitFont, availableWidth, fontSize);
        const isBlank = fitted.text === '';
        const textX = annotation.x;
        const textY = inkBox.baselineY;

        return `
            <g>
                <rect x="${rectX}" y="${rectY}" width="${rectWidth}" height="${rectHeight}" fill="${fillColor}" stroke="transparent" stroke-width="0"></rect>
                ${isBlank ? '' : `<text x="${textX}" y="${textY}" fill="${normalizeInkColor(annotation.color || '#111827')}" font-size="${fitted.size}" font-family="${fontFamilyAttr}" font-weight="${fontWeight}" font-style="${fontStyle}" text-anchor="start">${escapeSvg(fitted.text)}</text>`}
            </g>
        `;
    }

    if (annotation.type === 'comment') {
        const bounds = getCommentAnnotationBounds(annotation);
        const isSelected = annotation.id === state.selectedAnnotationId
            && state.activeSuiteTool === 'edit'
            && (state.activeTool === 'comment' || state.activeTool === 'pointer');
        const centerX = bounds.x + (bounds.width / 2);
        const centerY = bounds.y + (bounds.height / 2);
        const previewRaw = String(annotation.text || '').trim();
        const preview = previewRaw.length > 42 ? `${previewRaw.slice(0, 42)}...` : previewRaw;
        const previewWidth = Math.min(300, Math.max(110, preview.length * 6.4 + 18));
        const previewX = bounds.x + bounds.width + 6;
        const previewY = Math.max(0, bounds.y - 2);

        return `
            <g>
                <circle cx="${centerX}" cy="${centerY}" r="${bounds.width / 2}" fill="#fef3c7" stroke="${isSelected ? '#1d4ed8' : '#f59e0b'}" stroke-width="${isSelected ? 1.8 : 1.2}" />
                <text x="${centerX}" y="${centerY + 4}" fill="#92400e" font-size="11" font-family="Helvetica, Arial, sans-serif" text-anchor="middle" font-weight="700">C</text>
                ${isSelected ? `<rect x="${previewX}" y="${previewY}" width="${previewWidth}" height="24" rx="5" ry="5" fill="#fffbeb" stroke="#f59e0b" stroke-width="1.1"></rect>` : ''}
                ${isSelected ? `<text x="${previewX + 8}" y="${previewY + 16}" fill="#78350f" font-size="11" font-family="Helvetica, Arial, sans-serif">${escapeSvg(preview || 'Comment')}</text>` : ''}
            </g>
        `;
    }

    if (annotation.type === 'shape') {
        const isSelected = annotation.id === state.selectedAnnotationId
            && state.activeSuiteTool === 'edit'
            && (state.activeTool === 'shape' || state.activeTool === 'pointer');

        return `<rect x="${annotation.x}" y="${annotation.y}" width="${annotation.width}" height="${annotation.height}" fill="transparent" stroke="${isSelected ? '#2563eb' : annotation.color}" stroke-width="${isSelected ? Math.max(1.2, annotation.stroke + 0.8) : annotation.stroke}" ${isSelected ? 'stroke-dasharray="5 3"' : ''} />`;
    }

    if (annotation.type === 'highlight') {
        const isSelected = annotation.id === state.selectedAnnotationId
            && state.activeSuiteTool === 'edit'
            && (state.activeTool === 'highlight' || state.activeTool === 'pointer');

        return `<rect x="${annotation.x}" y="${annotation.y}" width="${annotation.width}" height="${annotation.height}" fill="${annotation.color}" fill-opacity="0.35" stroke="${isSelected ? '#2563eb' : 'none'}" stroke-width="${isSelected ? '1.2' : '0'}" ${isSelected ? 'stroke-dasharray="4 3"' : ''} />`;
    }

    if (annotation.type === 'erase') {
        return `<rect x="${annotation.x}" y="${annotation.y}" width="${annotation.width}" height="${annotation.height}" fill="#ffffff" stroke="#d1d5db" stroke-dasharray="4 3" stroke-width="1" />`;
    }

    if (annotation.type === 'redact') {
        return `<rect x="${annotation.x}" y="${annotation.y}" width="${annotation.width}" height="${annotation.height}" fill="#000000" stroke="none" />`;
    }

    if (annotation.type === 'underline') {
        const lineY = annotation.y + annotation.stroke;
        const isSelected = annotation.id === state.selectedAnnotationId
            && state.activeSuiteTool === 'edit'
            && (state.activeTool === 'underline' || state.activeTool === 'pointer');
        const strokeColor = isSelected ? '#2563eb' : annotation.color;
        const strokeWidth = isSelected ? Math.max(1.5, annotation.stroke + 1) : annotation.stroke;

        return `<line x1="${annotation.x}" y1="${lineY}" x2="${annotation.x + annotation.width}" y2="${lineY}" stroke="${strokeColor}" stroke-width="${strokeWidth}" />`;
    }

    if (annotation.type === 'draw') {
        const points = annotation.points || [];
        if (points.length < 2) {
            return '';
        }
        const d = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
        const isSelected = annotation.id === state.selectedAnnotationId
            && state.activeSuiteTool === 'edit'
            && (state.activeTool === 'draw' || state.activeTool === 'pointer');
        const strokeColor = isSelected ? '#2563eb' : annotation.color;
        const strokeWidth = isSelected ? Math.max(1.5, annotation.stroke + 1) : annotation.stroke;

        return `<path d="${d}" stroke="${strokeColor}" stroke-width="${strokeWidth}" fill="none" stroke-linecap="round" stroke-linejoin="round" ${isSelected ? 'stroke-dasharray="4 2"' : ''} />`;
    }

    if (annotation.type === 'image') {
        const bounds = getImageAnnotationBounds(annotation);
        const isSelected = annotation.id === state.selectedAnnotationId
            && state.activeSuiteTool === 'edit'
            && (state.activeTool === 'image' || state.activeTool === 'pointer');
        if (!isSelected) {
            return `<image href="${annotation.dataUrl}" x="${bounds.x}" y="${bounds.y}" width="${bounds.width}" height="${bounds.height}" />`;
        }

        const handle = getImageResizeHandle(annotation);
        return `
            <g>
                <image href="${annotation.dataUrl}" x="${bounds.x}" y="${bounds.y}" width="${bounds.width}" height="${bounds.height}" />
                <rect x="${bounds.x}" y="${bounds.y}" width="${bounds.width}" height="${bounds.height}" fill="rgba(59,130,246,0.05)" stroke="#3b82f6" stroke-width="1.2" stroke-dasharray="4 3"></rect>
                <rect x="${handle.x}" y="${handle.y}" width="${handle.width}" height="${handle.height}" rx="2" ry="2" fill="#3b82f6" stroke="#ffffff" stroke-width="1"></rect>
            </g>
        `;
    }

    return '';
}

export function redrawPage(pageNumber) {
    const shell = pagesEl.querySelector(`[data-page="${pageNumber}"]`);
    if (!shell) {
        return;
    }

    const svg = shell.querySelector('.pdf-annotation-layer');
    const layout = state.pageLayouts.get(pageNumber);
    if (!layout) {
        return;
    }

    svg.setAttribute('viewBox', `0 0 ${layout.screenWidth} ${layout.screenHeight}`);
    svg.setAttribute('width', String(layout.screenWidth));
    svg.setAttribute('height', String(layout.screenHeight));

    const pageAnnotations = state.annotations.filter((annotation) => annotation.page === pageNumber);
    const draft = state.draft && state.draft.page === pageNumber ? [state.draft] : [];
    const formFieldSvg = getFormFieldOverlaySvg(pageNumber);
    const watermarkSvg = getWatermarkPreviewSvg(pageNumber, layout);
    svg.innerHTML = formFieldSvg + pageAnnotations.concat(draft).map(getAnnotationSvg).join('') + watermarkSvg + getFindOverlaySvg(pageNumber);
}

export function redrawAllPages() {
    ensureSelectionStillExists();
    for (const [pageNumber] of state.pageLayouts.entries()) {
        redrawPage(pageNumber);
    }
    // Selection changes always come through here; the properties panel
    // follows the selection.
    syncPropsPanel();
}

export function startDragInteraction(tool, pageNumber, point, pointerId, pageShell) {
    state.interaction = {
        kind: 'drag',
        tool,
        page: pageNumber,
        startX: point.x,
        startY: point.y,
        pointerId,
        pageShell,
    };

    const base = {
        id: generateId(),
        type: tool,
        page: pageNumber,
        x: point.x,
        y: point.y,
        width: 1,
        height: 1,
        color: getToolColor(),
        stroke: Math.max(1, Math.round(getToolSize() / 4)),
    };

    if (tool === 'underline') {
        base.height = base.stroke;
    }

    state.draft = base;
    redrawPage(pageNumber);
}

export function startDrawInteraction(pageNumber, point, pointerId, pageShell) {
    state.interaction = {
        kind: 'draw',
        page: pageNumber,
        pointerId,
        pageShell,
    };

    state.draft = {
        id: generateId(),
        type: 'draw',
        page: pageNumber,
        points: [point],
        color: getToolColor(),
        stroke: Math.max(1, Math.round(getToolSize() / 4)),
    };

    redrawPage(pageNumber);
}

export function finishInteraction() {
    const draft = state.draft;
    if (!draft) {
        state.interaction = null;
        return;
    }

    let valid = false;

    if (draft.type === 'draw') {
        valid = (draft.points || []).length > 1;
    } else if (draft.type === 'underline') {
        valid = draft.width >= 8;
    } else {
        valid = draft.width >= 6 && draft.height >= 6;
    }

    if (valid) {
        pushHistory();
        state.annotations.push(draft);
        setStatus(`Added ${draft.type} annotation`);
    }

    state.draft = null;
    state.interaction = null;
    redrawAllPages();
}

export function onPagePointerDown(event) {
    const pageShell = event.currentTarget;
    const pageNumber = Number(pageShell.dataset.page);
    state.lastActivePage = pageNumber;
    setActiveThumb(pageNumber);
    syncPageIndicator(pageNumber);

    if (!isPagePrepared(pageNumber)) {
        // Text fragments and fields for this page are not extracted yet.
        event.preventDefault();
        ensurePagePrepared(pageNumber).then(() => {
            if (isPagePrepared(pageNumber)) {
                setStatus(`Page ${pageNumber} ready`);
            }
        }).catch(() => {});
        setStatus(`Preparing page ${pageNumber}...`);
        return;
    }

    const point = getPagePoint(event, pageShell);

    if (state.activeSuiteTool !== 'edit') {
        event.preventDefault();
        const now = Date.now();
        if (now - state.lastModeAlertAt > 1800) {
            const label = suiteModeLabels[state.activeSuiteTool] || 'This';
            showAlert(`${label} tools are coming next. Select Edit to use the active toolbar.`);
            state.lastModeAlertAt = now;
        }
        setStatus(`${suiteModeLabels[state.activeSuiteTool] || 'Tool'} mode selected`);
        return;
    }

    const tool = state.activeTool;

    if (tool === 'replace' && !replaceToolEnabled) {
        activateTool('text');
        event.preventDefault();
        return;
    }

    if (state.activeTextEditor && tool !== 'text') {
        commitInlineTextEditor(true);
    }

    if (state.activeCommentEditor && tool !== 'comment') {
        commitInlineCommentEditor(true);
    }

    if (state.activeReplaceEditor && tool !== 'replace') {
        commitInlineReplaceEditor(true);
    }

    if (state.activeBlockEditor && tool !== 'replace') {
        commitBlockTextEditor(true);
    }

    if (state.activeFormFieldEditor && tool !== 'form-fill') {
        commitInlineFormFieldEditor(true);
    }

    if (tool === 'explain') {
        event.preventDefault();
        explainAtPoint(pageNumber, point).catch(() => {});
        return;
    }

    if (tool === 'pointer') {
        event.preventDefault();
        const hitAnnotation = findSelectableAnnotationAtPoint(pageNumber, point.x, point.y);
        if (!hitAnnotation) {
            setSelectedAnnotation(null, false);
            setStatus('Pointer ready');
            return;
        }

        if (hitAnnotation.type === 'text') {
            activateTool('text');
            setSelectedAnnotation(hitAnnotation.id, true);
            setStatus('Text selected. Drag to move or double-click to edit.');
            return;
        }

        if (hitAnnotation.type === 'edit-block') {
            if (event.detail >= 2) {
                activateTool('replace');
                openBlockTextEditor(
                    pageShell,
                    pageNumber,
                    findTextBlockById(pageNumber, hitAnnotation.sourceBlockId),
                    hitAnnotation,
                    event
                );
                setStatus('Editing text block. Click away or press Done to apply; Esc cancels.');
                return;
            }

            setSelectedAnnotation(hitAnnotation.id, false);
            setStatus('Text edit selected. Double-click to edit again, or press Delete to restore the original text.');
            return;
        }

        setSelectedAnnotation(hitAnnotation.id, false);
        setStatus(`${getAnnotationTypeLabel(hitAnnotation)} selected. Press Delete to remove.`);
        return;
    }

    if (tool === 'form-fill') {
        event.preventDefault();

        if (state.activeFormFieldEditor) {
            commitInlineFormFieldEditor(true);
        }

        const fields = getFormFieldsForPage(pageNumber);
        if (!fields.length) {
            setStatus('No fillable fields on this page. Click Detect Fields in Form Fill to scan underlines/lines/checkboxes.');
            return;
        }

        const hitField = findFormFieldAtPoint(pageNumber, point.x, point.y);
        if (!hitField) {
            setStatus('Form fill: click inside a highlighted field');
            return;
        }

        if (hitField.readOnly) {
            showAlert('This field is read-only and cannot be edited.');
            setStatus('Read-only field');
            return;
        }

        if (hitField.kind === 'checkbox') {
            toggleCheckboxFormField(hitField, pageNumber);
            return;
        }

        openInlineFormFieldEditor(pageShell, pageNumber, hitField);
        return;
    }

    if (tool === 'replace') {
        event.preventDefault();
        if (state.activeReplaceEditor || state.activeBlockEditor) {
            commitInlineReplaceEditor(true);
        }

        // Re-open a committed block edit for further editing.
        const existingBlockEdit = findEditBlockAnnotationAtPoint(pageNumber, point.x, point.y);
        if (existingBlockEdit) {
            openBlockTextEditor(
                pageShell,
                pageNumber,
                findTextBlockById(pageNumber, existingBlockEdit.sourceBlockId),
                existingBlockEdit,
                event
            );
            setStatus('Editing text block. Click away or press Done to apply; Esc cancels.');
            return;
        }

        if (pageTextLooksUnreadable(pageNumber)) {
            showAlert('This PDF does not contain readable text (common for files saved with "Print" on an iPhone or iPad), so its wording cannot be edited in place. Use Text to add new text, or upload the original file.', true);
            setStatus('Edit text: this page has no readable text');
            return;
        }

        // Primary path: open the whole text block as an editable document
        // region (real editing — delete lines, remove blank space, reflow).
        const block = findTextBlockAtPoint(pageNumber, point.x, point.y);
        if (block) {
            openBlockTextEditor(pageShell, pageNumber, block, null, event);
            setStatus('Editing text block. Click away or press Done to apply; Esc cancels.');
            return;
        }

        const existingReplacement = findReplaceAnnotationAtPoint(pageNumber, point.x, point.y);
        if (existingReplacement) {
            const fragment = {
                text: existingReplacement.originalText || '',
                x: Number(existingReplacement.x || 0),
                y: Number(existingReplacement.y || 0),
                width: Math.max(8, Number(existingReplacement.width || 8)),
                height: Math.max(8, Number(existingReplacement.height || 8)),
                fontSize: Math.max(8, Number(existingReplacement.fontSize || 12)),
                fontFamily: existingReplacement.fontFamily || 'Helvetica',
                cssFontFamily: existingReplacement.cssFontFamily || '',
                bold: !!existingReplacement.bold,
                italic: !!existingReplacement.italic,
                color: normalizeInkColor(existingReplacement.color || '#111827'),
                backgroundColor: normalizeBackgroundColor(existingReplacement.backgroundColor || '#ffffff'),
                align: existingReplacement.align || 'left',
                baselineY: Number.isFinite(Number(existingReplacement.baselineY))
                    ? Number(existingReplacement.baselineY)
                    : Number(existingReplacement.y || 0) + Math.max(8, Number(existingReplacement.fontSize || 12)) * 0.86,
                baselineOffset: Number.isFinite(Number(existingReplacement.baselineOffset))
                    ? Number(existingReplacement.baselineOffset)
                    : Math.max(8, Number(existingReplacement.fontSize || 12)) * 0.86,
                ascentRatio: Number.isFinite(Number(existingReplacement.ascentRatio)) ? Number(existingReplacement.ascentRatio) : 0.86,
                descentRatio: Number.isFinite(Number(existingReplacement.descentRatio)) ? Number(existingReplacement.descentRatio) : 0.14,
            };
            openInlineReplaceEditor(pageShell, pageNumber, fragment, existingReplacement);
            setStatus('Edit the text in place. Enter saves; Escape cancels.');
            return;
        }

        const fragment = findTextFragmentAtPoint(pageNumber, point.x, point.y);
        if (!fragment) {
            const pageFragments = state.pageTextFragments.get(pageNumber) || [];
            if (!pageFragments.length) {
                showAlert('No selectable text found on this page. This may be a scanned PDF image.', true);
            } else {
                showAlert('Click directly on existing text to replace it.');
            }
            setStatus('Replace text: select existing text');
            return;
        }

        openInlineReplaceEditor(pageShell, pageNumber, buildReplacementFragment(pageNumber, fragment));
        setStatus('Edit the text in place. Backspace can clear it; Enter saves.');
        return;
    }

    if (tool === 'text') {
        event.preventDefault();

        if (state.activeTextEditor) {
            commitInlineTextEditor(true);
        }

        const hitAnnotation = findTextAnnotationAtPoint(pageNumber, point.x, point.y);

        if (hitAnnotation) {
            setSelectedAnnotation(hitAnnotation.id, true);

            if (event.detail >= 2) {
                openInlineTextEditor(pageShell, pageNumber, point, hitAnnotation);
                setStatus('Editing selected text...');
                return;
            }

            pageShell.setPointerCapture(event.pointerId);
            state.interaction = {
                kind: 'move-text',
                page: pageNumber,
                pointerId: event.pointerId,
                annotationId: hitAnnotation.id,
                offsetX: point.x - Number(hitAnnotation.x || 0),
                offsetY: point.y - Number(hitAnnotation.y || 0),
                moved: false,
                historyPushed: false,
                startX: Number(hitAnnotation.x || 0),
                startY: Number(hitAnnotation.y || 0),
            };

            setStatus('Text selected. Drag to reposition.');
            return;
        }

        clearTextSelection();
        openInlineTextEditor(pageShell, pageNumber, point);
        setStatus('Typing text...');
        return;
    }

    if (tool === 'comment') {
        event.preventDefault();
        if (state.activeCommentEditor) {
            commitInlineCommentEditor(true);
        }

        const hitComment = findCommentAnnotationAtPoint(pageNumber, point.x, point.y);
        if (hitComment) {
            setSelectedAnnotation(hitComment.id, false);

            if (event.detail >= 2) {
                openInlineCommentEditor(pageShell, pageNumber, point, hitComment);
                setStatus('Editing comment...');
                return;
            }

            const bounds = getCommentAnnotationBounds(hitComment);
            pageShell.setPointerCapture(event.pointerId);
            state.interaction = {
                kind: 'move-comment',
                page: pageNumber,
                pointerId: event.pointerId,
                annotationId: hitComment.id,
                offsetX: point.x - Number(hitComment.x || 0),
                offsetY: point.y - Number(hitComment.y || 0),
                moved: false,
                historyPushed: false,
                startX: Number(hitComment.x || 0),
                startY: Number(hitComment.y || 0),
                boundsWidth: bounds.width,
                boundsHeight: bounds.height,
            };

            setStatus('Comment selected. Drag to reposition.');
            return;
        }

        setSelectedAnnotation(null, false);
        openInlineCommentEditor(pageShell, pageNumber, point);
        setStatus('Adding comment...');
        return;
    }

    if (tool === 'image') {
        event.preventDefault();
        const hitImage = findImageAnnotationAtPoint(pageNumber, point.x, point.y);
        if (hitImage) {
            setSelectedAnnotation(hitImage.id, false);
            pageShell.setPointerCapture(event.pointerId);

            if (isPointInImageResizeHandle(hitImage, point.x, point.y)) {
                state.interaction = {
                    kind: 'resize-image',
                    page: pageNumber,
                    pointerId: event.pointerId,
                    annotationId: hitImage.id,
                    startWidth: Number(hitImage.width || 1),
                    startHeight: Number(hitImage.height || 1),
                    aspectRatio: Math.max(0.01, Number(hitImage.width || 1) / Math.max(1, Number(hitImage.height || 1))),
                    historyPushed: false,
                    moved: false,
                };

                setStatus('Drag to resize image');
                return;
            }

            state.interaction = {
                kind: 'move-image',
                page: pageNumber,
                pointerId: event.pointerId,
                annotationId: hitImage.id,
                offsetX: point.x - Number(hitImage.x || 0),
                offsetY: point.y - Number(hitImage.y || 0),
                moved: false,
                historyPushed: false,
                startX: Number(hitImage.x || 0),
                startY: Number(hitImage.y || 0),
            };

            setStatus('Image selected. Drag to reposition.');
            return;
        }

        if (!state.pendingImage) {
            imageInput.click();
            setStatus('Pick an image first');
            return;
        }

        const maxWidth = Math.min(240, point.width * 0.45);
        const width = maxWidth;
        const height = width / state.pendingImage.aspectRatio;

        const imagePayload = {
            id: generateId(),
            type: 'image',
            page: pageNumber,
            x: clamp(point.x, 0, Math.max(1, point.width - width)),
            y: clamp(point.y, 0, Math.max(1, point.height - height)),
            width,
            height,
            dataUrl: state.pendingImage.dataUrl,
            mimeType: state.pendingImage.mimeType,
        };

        pushHistory();
        state.annotations.push(imagePayload);
        setSelectedAnnotation(imagePayload.id, false);
        redrawPage(pageNumber);
        setStatus('Image placed. Drag to move or resize.');
        return;
    }

    if (tool === 'draw') {
        event.preventDefault();
        pageShell.setPointerCapture(event.pointerId);
        startDrawInteraction(pageNumber, point, event.pointerId, pageShell);
        return;
    }

    if (['shape', 'highlight', 'erase', 'redact', 'underline'].includes(tool)) {
        event.preventDefault();
        pageShell.setPointerCapture(event.pointerId);
        startDragInteraction(tool, pageNumber, point, event.pointerId, pageShell);
    }
}

export function onPagePointerMove(event) {
    if (!state.interaction) {
        return;
    }

    const pageShell = event.currentTarget;
    const pageNumber = Number(pageShell.dataset.page);
    if (state.interaction.page !== pageNumber) {
        return;
    }

    const point = getPagePoint(event, pageShell);

    if (state.interaction.kind === 'move-text') {
        event.preventDefault();
        const annotation = state.annotations.find((item) => item.id === state.interaction.annotationId && item.type === 'text');
        if (!annotation) {
            return;
        }

        const bounds = getTextAnnotationBounds(annotation);
        const nextX = clamp(point.x - state.interaction.offsetX, 0, Math.max(1, point.width - bounds.width));
        const nextY = clamp(point.y - state.interaction.offsetY, 0, Math.max(1, point.height - bounds.height));

        const deltaX = Math.abs(nextX - state.interaction.startX);
        const deltaY = Math.abs(nextY - state.interaction.startY);
        const hasMoved = deltaX > 0.8 || deltaY > 0.8;

        if (hasMoved && !state.interaction.historyPushed) {
            pushHistory();
            state.interaction.historyPushed = true;
        }

        annotation.x = nextX;
        annotation.y = nextY;
        state.interaction.moved = state.interaction.moved || hasMoved;
        redrawPage(pageNumber);
        return;
    }

    if (state.interaction.kind === 'move-comment') {
        event.preventDefault();
        const annotation = state.annotations.find((item) => item.id === state.interaction.annotationId && item.type === 'comment');
        if (!annotation) {
            return;
        }

        const nextX = clamp(
            point.x - state.interaction.offsetX,
            0,
            Math.max(1, point.width - Number(state.interaction.boundsWidth || 22))
        );
        const nextY = clamp(
            point.y - state.interaction.offsetY,
            0,
            Math.max(1, point.height - Number(state.interaction.boundsHeight || 22))
        );

        const deltaX = Math.abs(nextX - state.interaction.startX);
        const deltaY = Math.abs(nextY - state.interaction.startY);
        const hasMoved = deltaX > 0.8 || deltaY > 0.8;

        if (hasMoved && !state.interaction.historyPushed) {
            pushHistory();
            state.interaction.historyPushed = true;
        }

        annotation.x = nextX;
        annotation.y = nextY;
        state.interaction.moved = state.interaction.moved || hasMoved;
        redrawPage(pageNumber);
        return;
    }

    if (state.interaction.kind === 'move-image') {
        event.preventDefault();
        const annotation = state.annotations.find((item) => item.id === state.interaction.annotationId && item.type === 'image');
        if (!annotation) {
            return;
        }

        const bounds = getImageAnnotationBounds(annotation);
        const nextX = clamp(point.x - state.interaction.offsetX, 0, Math.max(1, point.width - bounds.width));
        const nextY = clamp(point.y - state.interaction.offsetY, 0, Math.max(1, point.height - bounds.height));

        const deltaX = Math.abs(nextX - state.interaction.startX);
        const deltaY = Math.abs(nextY - state.interaction.startY);
        const hasMoved = deltaX > 0.8 || deltaY > 0.8;

        if (hasMoved && !state.interaction.historyPushed) {
            pushHistory();
            state.interaction.historyPushed = true;
        }

        annotation.x = nextX;
        annotation.y = nextY;
        state.interaction.moved = state.interaction.moved || hasMoved;
        redrawPage(pageNumber);
        return;
    }

    if (state.interaction.kind === 'resize-image') {
        event.preventDefault();
        const annotation = state.annotations.find((item) => item.id === state.interaction.annotationId && item.type === 'image');
        if (!annotation) {
            return;
        }

        const minWidth = 24;
        const minHeight = 24;
        const aspectRatio = Math.max(0.01, Number(state.interaction.aspectRatio || 1));
        const anchorX = Number(annotation.x || 0);
        const anchorY = Number(annotation.y || 0);

        let nextWidth = clamp(point.x - anchorX, minWidth, Math.max(minWidth, point.width - anchorX));
        let nextHeight = nextWidth / aspectRatio;

        if (anchorY + nextHeight > point.height) {
            nextHeight = Math.max(minHeight, point.height - anchorY);
            nextWidth = nextHeight * aspectRatio;
        }

        if (nextHeight < minHeight) {
            nextHeight = minHeight;
            nextWidth = nextHeight * aspectRatio;
        }

        if (anchorX + nextWidth > point.width) {
            nextWidth = Math.max(minWidth, point.width - anchorX);
            nextHeight = nextWidth / aspectRatio;
        }

        const deltaW = Math.abs(nextWidth - state.interaction.startWidth);
        const deltaH = Math.abs(nextHeight - state.interaction.startHeight);
        const hasMoved = deltaW > 0.8 || deltaH > 0.8;

        if (hasMoved && !state.interaction.historyPushed) {
            pushHistory();
            state.interaction.historyPushed = true;
        }

        annotation.width = nextWidth;
        annotation.height = nextHeight;
        state.interaction.moved = state.interaction.moved || hasMoved;
        redrawPage(pageNumber);
        return;
    }

    if (state.interaction.kind === 'draw') {
        event.preventDefault();
        const draft = state.draft;
        if (!draft || draft.type !== 'draw') {
            return;
        }

        const points = draft.points;
        const last = points[points.length - 1];
        const deltaX = Math.abs(last.x - point.x);
        const deltaY = Math.abs(last.y - point.y);
        if (deltaX >= 1 || deltaY >= 1) {
            points.push(point);
            redrawPage(pageNumber);
        }
        return;
    }

    if (state.interaction.kind === 'drag') {
        event.preventDefault();
        const draft = state.draft;
        if (!draft) {
            return;
        }

        const startX = state.interaction.startX;
        const startY = state.interaction.startY;

        if (draft.type === 'underline') {
            draft.x = Math.min(startX, point.x);
            draft.y = startY;
            draft.width = Math.abs(point.x - startX);
            draft.height = draft.stroke;
        } else {
            draft.x = Math.min(startX, point.x);
            draft.y = Math.min(startY, point.y);
            draft.width = Math.abs(point.x - startX);
            draft.height = Math.abs(point.y - startY);
        }

        redrawPage(pageNumber);
    }
}

export function onPagePointerUp(event) {
    if (!state.interaction) {
        return;
    }

    const interactionKind = state.interaction.kind;
    const pageShell = event.currentTarget;
    if (state.interaction.pointerId != null) {
        try {
            pageShell.releasePointerCapture(state.interaction.pointerId);
        } catch (error) {
            // no-op
        }
    }

    if (interactionKind === 'move-text') {
        const didMove = !!state.interaction.moved;
        state.interaction = null;
        setStatus(didMove ? 'Text moved' : 'Text selected');
        return;
    }

    if (interactionKind === 'move-comment') {
        const didMove = !!state.interaction.moved;
        state.interaction = null;
        setStatus(didMove ? 'Comment moved' : 'Comment selected');
        return;
    }

    if (interactionKind === 'move-image') {
        const didMove = !!state.interaction.moved;
        state.interaction = null;
        setStatus(didMove ? 'Image moved' : 'Image selected');
        return;
    }

    if (interactionKind === 'resize-image') {
        const didResize = !!state.interaction.moved;
        state.interaction = null;
        setStatus(didResize ? 'Image resized' : 'Image selected');
        return;
    }

    finishInteraction();
}

export function onPagePointerLeave(event) {
    if (!state.interaction) {
        return;
    }

    if (state.interaction.pointerId !== event.pointerId) {
        return;
    }

    onPagePointerUp(event);
}

export function onPageTouchStart(event) {
    // Pages allow native finger scrolling (touch-action: manipulation).
    // When a finger lands on an existing annotation in a selection tool,
    // keep the gesture for dragging it instead of scrolling the page.
    if (state.activeSuiteTool !== 'edit' || !event.touches || event.touches.length !== 1) {
        return;
    }
    const tool = state.activeTool;
    if (tool !== 'pointer' && tool !== 'text' && tool !== 'comment' && tool !== 'image') {
        return;
    }
    const pageShell = event.currentTarget;
    const touch = event.touches[0];
    const point = getPagePoint({ clientX: touch.clientX, clientY: touch.clientY }, pageShell);
    const hit = findSelectableAnnotationAtPoint(Number(pageShell.dataset.page), point.x, point.y);
    if (hit && event.cancelable) {
        event.preventDefault();
    }
}

export function undoAction() {
    commitAllInlineEditors();

    if (!state.history.length || state.historyBusy) {
        return;
    }

    const entry = state.history.pop();
    if (entry && entry.kind === 'document') {
        applyDocumentEntry(entry, 'undo').catch(() => {});
        return;
    }

    state.redo.push({ kind: 'edit', label: entry.label, snapshot: captureEditSnapshot() });
    restoreEditSnapshot(typeof entry === 'string' ? entry : entry.snapshot);
    state.annotationDirty = true;
    updateUndoRedoButtons();
    redrawAllPages();
    updateFormFillSummary();
    scheduleDraftSave();
    setStatus(`Undo complete${entryLabel(entry) ? `:${entryLabel(entry)}` : ''}`);
}

export function redoAction() {
    commitAllInlineEditors();

    if (!state.redo.length || state.historyBusy) {
        return;
    }

    const entry = state.redo.pop();
    if (entry && entry.kind === 'document') {
        applyDocumentEntry(entry, 'redo').catch(() => {});
        return;
    }

    state.history.push({ kind: 'edit', label: entry.label, snapshot: captureEditSnapshot() });
    restoreEditSnapshot(typeof entry === 'string' ? entry : entry.snapshot);
    state.annotationDirty = true;
    updateUndoRedoButtons();
    redrawAllPages();
    updateFormFillSummary();
    scheduleDraftSave();
    setStatus(`Redo complete${entryLabel(entry) ? `:${entryLabel(entry)}` : ''}`);
}
